import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Eye, Pause, Pencil, Play, Trash2 } from 'lucide-react';
import {
  PortalDsBadge,
  PortalDsButton,
  portalDs,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { CampaignEmailSchedule } from '../types/campaign-email.types';
import {
  formatDayOfWeekLabel,
  formatFrequencyLabel,
  summariseSelectedCampaigns,
} from '../utils/campaign-email.mappers';

interface CampaignEmailSchedulesTableProps {
  schedules: CampaignEmailSchedule[];
  onEdit: (schedule: CampaignEmailSchedule) => void;
  onPreview: (schedule: CampaignEmailSchedule) => void;
  onPauseResume: (schedule: CampaignEmailSchedule) => void;
  onDelete: (schedule: CampaignEmailSchedule) => void;
  isMutating?: boolean;
}

function formatLastSent(value: string | null): string {
  if (!value) {
    return 'Never';
  }
  return dayjs(value).format('D MMM YYYY, h:mm A');
}

export function CampaignEmailSchedulesTable({
  schedules,
  onEdit,
  onPreview,
  onPauseResume,
  onDelete,
  isMutating = false,
}: CampaignEmailSchedulesTableProps) {
  const sortedSchedules = useMemo(
    () => [...schedules].sort((left, right) => right.updated_at.localeCompare(left.updated_at)),
    [schedules]
  );

  if (sortedSchedules.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[#EDEEF2] bg-white px-6 py-12 text-center">
        <p className="text-[15px] font-medium text-[#0D0F1A]">
          No email schedules yet
        </p>
        <p className={cn(portalDs.bodySm, 'mt-2')}>
          Create a schedule to send automated campaign performance emails.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-[#EDEEF2] bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[#EDEEF2]">
          <thead className="bg-[#F8F9FB]">
            <tr>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]">
                Schedule
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]">
                Client
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]">
                Campaigns
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]">
                Frequency
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]">
                Status
              </th>
              <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]">
                Last sent
              </th>
              <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-[0.5px] text-[#9CA3AF]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EDEEF2]">
            {sortedSchedules.map((schedule) => {
              const isPaused = schedule.schedule_status === 'paused';
              return (
                <tr
                  key={schedule.id}
                  className="hover:bg-[#FAFBFC]"
                >
                  <td className="px-4 py-4">
                    <div className="text-[13px] font-semibold text-[#0D0F1A]">
                      {schedule.name}
                    </div>
                    <div className={portalDs.caption}>
                      {schedule.recipients.length}
                      {' '}
                      recipient
                      {schedule.recipients.length === 1 ? '' : 's'}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-[13px] text-[#0D0F1A]">
                    {schedule.client_title || '—'}
                  </td>
                  <td className="px-4 py-4 text-[13px] text-[#5E6278]">
                    {summariseSelectedCampaigns(schedule.selected_campaigns)}
                  </td>
                  <td className="px-4 py-4 text-[13px] text-[#5E6278]">
                    {formatFrequencyLabel(schedule.frequency)}
                    {schedule.frequency === 'daily' && schedule.start_date && schedule.end_date ? (
                      <div className={portalDs.caption}>
                        {schedule.start_date}
                        {' '}
                        –
                        {' '}
                        {schedule.end_date}
                      </div>
                    ) : (
                      <div className={portalDs.caption}>
                        {formatDayOfWeekLabel(schedule.day_of_week)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <PortalDsBadge
                      variant={isPaused ? 'amber' : 'green'}
                      pill
                    >
                      {isPaused ? 'Paused' : 'Active'}
                    </PortalDsBadge>
                  </td>
                  <td className="px-4 py-4 text-[13px] text-[#5E6278]">
                    {formatLastSent(schedule.last_sent_at)}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <PortalDsButton
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={isMutating}
                        aria-label={`Preview ${schedule.name}`}
                        onClick={() => onPreview(schedule)}
                      >
                        <Eye
                          className="size-4"
                          aria-hidden
                        />
                      </PortalDsButton>
                      <PortalDsButton
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={isMutating}
                        aria-label={`Edit ${schedule.name}`}
                        onClick={() => onEdit(schedule)}
                      >
                        <Pencil
                          className="size-4"
                          aria-hidden
                        />
                      </PortalDsButton>
                      <PortalDsButton
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={isMutating}
                        aria-label={isPaused ? `Resume ${schedule.name}` : `Pause ${schedule.name}`}
                        onClick={() => onPauseResume(schedule)}
                      >
                        {isPaused ? (
                          <Play
                            className="size-4"
                            aria-hidden
                          />
                        ) : (
                          <Pause
                            className="size-4"
                            aria-hidden
                          />
                        )}
                      </PortalDsButton>
                      <PortalDsButton
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={isMutating}
                        aria-label={`Delete ${schedule.name}`}
                        onClick={() => onDelete(schedule)}
                      >
                        <Trash2
                          className="size-4 text-[#DC2626]"
                          aria-hidden
                        />
                      </PortalDsButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
